import React, { useEffect } from '@wordpress/element';
import * as Sentry from '@sentry/react';

const ErrorComponent = ({ error, componentStack, resetError, componentName, parentName, ...additionalProps }) => {
    useEffect(() => {
        // Single point of error capture with comprehensive context
        Sentry.withScope((scope) => {
            scope.setLevel('fatal'); // Use 'fatal' for critical errors
            
            // Add all relevant context
            scope.setExtra('componentStack', componentStack);
            scope.setExtra('errorLocation', window.location.href);
            scope.setExtra('timestamp', new Date().toISOString());
            scope.setTag('error_type', 'react_error_boundary');
            scope.setTag('error_category', 'critical_render_failure');
            scope.setTag('manually_captured', true);
            
            // Add component-specific context if provided
            if (componentName) {
                scope.setTag('component', componentName);
                scope.setTag('failed_component', componentName); // Make it more visible
            }
            if (parentName) {
                scope.setTag('parent', parentName);
            }
            
            // Set a clear fingerprint for these critical errors
            scope.setFingerprint([
                'react_error_boundary',
                componentName || 'unknown_component',
                error?.name || 'Error',
                error?.message?.slice(0, 50) || 'unknown'
            ]);
            
            // Add component-specific context if provided
            if (componentName) {
                scope.setTag('component', componentName);
            }
            if (parentName) {
                scope.setTag('parent', parentName);
            }
            
            // Try to extract more detailed error location from component stack
            if (componentStack) {
                const stackLines = componentStack.split('\n');
                // Find the first non-ErrorBoundary component
                const errorComponent = stackLines.find(line => 
                    !line.includes('ErrorBoundary') && 
                    !line.includes('Suspense') &&
                    line.trim().startsWith('at ')
                );
                if (errorComponent) {
                    scope.setExtra('error_component_from_stack', errorComponent.trim());
                }
            }
            
            // Try to extract function/hook name from error stack
            if (error?.stack) {
                const stackLines = error.stack.split('\n');
                
                // Look for hook calls (use*, handle*, on*)
                const hookLine = stackLines.find(line => 
                    /\b(use[A-Z]\w*|handle\w*|on[A-Z]\w*)\b/.test(line)
                );
                if (hookLine) {
                    const hookMatch = hookLine.match(/\b(use[A-Z]\w*|handle\w*|on[A-Z]\w*)\b/);
                    if (hookMatch) {
                        scope.setTag('hook_or_handler', hookMatch[1]);
                    }
                }
                
                // Extract the most relevant function name (first function after the error)
                const functionMatch = stackLines[1]?.match(/at\s+(\w+)\s*\(/);
                if (functionMatch && functionMatch[1] !== 'Object') {
                    scope.setTag('error_function', functionMatch[1]);
                }
            }
            
            // Add any additional props as context
            const extraProps = Object.entries(additionalProps).reduce((acc, [key, value]) => {
                if (typeof value !== 'function' && key !== 'children') {
                    acc[key] = value;
                }
                return acc;
            }, {});
            
            if (Object.keys(extraProps).length > 0) {
                scope.setContext('additional_props', extraProps);
            }
            
            // Capture the exception
            Sentry.captureException(error, {
                tags: {
                    component_failure: true,
                    requires_immediate_attention: true
                }
            });
        });
        
        // Also log to console for local debugging
        console.error('React Error Boundary caught error:', {
            error,
            component: componentName,
            parent: parentName,
            componentStack
        });
        
        // Store minimal error info in localStorage for recovery/debugging
        try {
            localStorage.setItem('range-react-error', 'true');
            localStorage.setItem('range-react-error-time', new Date().toISOString());
            if (componentName) {
                localStorage.setItem('range-react-error-component', componentName);
            }
            if (error?.message) {
                localStorage.setItem('range-react-error-message', error.message.slice(0, 100));
            }
        } catch (e) {
            // Ignore localStorage errors
        }
    }, [error, componentStack, componentName, parentName]);

    const handleReset = () => {
        try {
            console.log('Resetting application and clearing storage...');
            
            // Clear storage using the same logic as the inline script
            const keysToPreserve = ['rangeHeadlessVersion']; // Preserve the version key
            
            Object.keys(localStorage).forEach(key => {
                if (!keysToPreserve.includes(key) && 
                    (key.startsWith("headless_") || 
                    key.startsWith("HEADLESS_") || 
                    key.includes("checkout") || 
                    key.includes("DUTCHIE") ||
                    key.includes("RANGE_HEADLESS") ||
                    key.includes("range-react-error"))) { // Also clear error-related keys
                    localStorage.removeItem(key);
                }
            });
            
            // Clear session storage completely
            sessionStorage.clear();
            
            // Set the clear cache flag
            sessionStorage.setItem("rangeClearCache", "1");
            
            // Try to reset the error boundary first
            if (resetError && typeof resetError === 'function') {
                resetError();
            }
            
            // If we're still here after a moment, force reload
            setTimeout(() => {
                window.location.reload(true);
            }, 100);
            
        } catch (resetError) {
            console.error('Failed to reset error boundary:', resetError);
            // Force reload as fallback
            window.location.reload(true);
        }
    };

    return (
        <div className='headless-error-component'>
            <div className='error-component-title'>
                Problem loading{componentName ? ` ${componentName}` : ''}:
            </div>
            <div role='button' onClick={handleReset} className='error-reset-button'>
                click to retry.
            </div>
        </div>
    );
};

export default ErrorComponent;
